import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Lock, Mail, Eye, EyeOff, Loader2, ArrowLeft, CheckCircle2, ShieldCheck, BadgeCheck, KeyRound, UserCircle2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { API_URL } from '../../utils/apiBase';

/**
 * Auth gate modal — shown when an unauthenticated user tries to do
 * something that requires an account (Book, send a chat message).
 *
 * Modes:
 *  1. signup  — name + email + password → POST /api/auth/register
 *  2. signin  — email + password         → POST /api/auth/login
 *  3. forgot  — email only                → POST /api/auth/forgot-password
 *  4. forgotSent — confirmation screen ("check your inbox")
 *
 * Plus the OAuth shortcuts (Google now, Facebook reserved).
 */
export default function AuthGateModal({ isOpen, onClose, returnTo }) {
  const { t } = useLanguage();
  const { user, signInWithGoogle, signInWithEmail, signUpWithEmail } = useAuth();

  const [mode, setMode] = useState('signup'); // 'signup' | 'signin' | 'forgot' | 'forgotSent'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(true); // sign-up defaults to on; login defaults set on mode switch
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password-strength score for the signup path only. Kept intentionally
  // simple + client-only — the backend already enforces min 6.
  const pwdScore = (() => {
    if (!password) return 0;
    let s = 0;
    if (password.length >= 6) s += 1;
    if (password.length >= 10) s += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) s += 1;
    if (/\d/.test(password)) s += 1;
    if (/[^A-Za-z0-9]/.test(password)) s += 1;
    return Math.min(s, 4); // 0-4
  })();
  const pwdMeter = (() => {
    if (pwdScore <= 1) return { label: t('authGate.pwdStrength.weak'), bar: 'w-1/4 bg-red-500', text: 'text-red-600' };
    if (pwdScore === 2) return { label: t('authGate.pwdStrength.fair'), bar: 'w-2/4 bg-amber-500', text: 'text-amber-700' };
    if (pwdScore === 3) return { label: t('authGate.pwdStrength.good'), bar: 'w-3/4 bg-emerald-500', text: 'text-emerald-700' };
    return { label: t('authGate.pwdStrength.strong'), bar: 'w-full bg-emerald-600', text: 'text-emerald-700' };
  })();

  // Defensive safety-net: if the user becomes authenticated WHILE this
  // modal is open (e.g. another tab logged in, a race condition let the
  // success path skip onClose, or the page re-rendered with a fresh user
  // from /api/auth/me), auto-dismiss the modal. Users complained that the
  // modal occasionally stayed up after a successful sign-in and they had
  // to hit the × manually — this guarantees it can't happen.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen) wasOpenRef.current = true;
    if (isOpen && user && wasOpenRef.current) {
      wasOpenRef.current = false;
      onClose && onClose();
    }
  }, [isOpen, user, onClose]);

  // Reset the form when the modal opens fresh — stale "loading" state
  // or a leftover error from a previous attempt should never greet the
  // user on a second visit.
  useEffect(() => {
    if (isOpen) {
      setLoading(false);
      setError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGoogle = () => {
    signInWithGoogle(returnTo || window.location.pathname + window.location.hash);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (!email || !password || (mode === 'signup' && !name)) {
      setError(t('authGate.fillFields'));
      return;
    }
    if (password.length < 6) {
      setError(t('authGate.passwordTooShort'));
      return;
    }
    setLoading(true);
    const fn = mode === 'signup' ? signUpWithEmail : signInWithEmail;
    const result = await fn({
      email: email.trim().toLowerCase(),
      password,
      name: name.trim(),
      rememberMe,
    });
    setLoading(false);
    if (result.ok) {
      onClose && onClose();
    } else {
      setError(result.error || t('authGate.genericError'));
    }
  };

  const switchMode = () => {
    setMode((m) => (m === 'signup' ? 'signin' : 'signup'));
    setError('');
  };

  const goToForgot = () => {
    setMode('forgot');
    setError('');
    setPassword('');
  };

  const goBackToSignIn = () => {
    setMode('signin');
    setError('');
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError(t('authGate.fillFields'));
      return;
    }
    setLoading(true);
    try {
      // Server always returns 200 to prevent email enumeration
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      setMode('forgotSent');
    } catch (err) {
      setError(err.message || t('authGate.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-start sm:items-center justify-center bg-black/65 backdrop-blur-md p-4 pt-8 sm:pt-4 overflow-y-auto animate-in fade-in"
      role="dialog"
      aria-modal="true"
      data-testid="auth-gate-modal"
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative animate-in zoom-in-95 my-auto">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          data-testid="auth-gate-close"
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-midnight-900 flex items-center justify-center shadow-lg shadow-midnight-900/25 ring-4 ring-gold-100">
          <Lock className="w-6 h-6 text-gold-400" />
        </div>

        <h2 className="text-2xl font-serif font-semibold text-center text-midnight-900 mb-1 tracking-tight" data-testid="auth-gate-title">
          {mode === 'signup' && t('authGate.titleSignup')}
          {mode === 'signin' && t('authGate.titleSignin')}
          {mode === 'forgot' && t('authGate.titleForgot')}
          {mode === 'forgotSent' && t('authGate.titleForgotSent')}
        </h2>
        <p className="text-sm text-center text-gray-500 mb-5">
          {mode === 'signup' && t('authGate.subtitleSignup')}
          {mode === 'signin' && t('authGate.subtitleSignin')}
          {mode === 'forgot' && t('authGate.subtitleForgot')}
          {mode === 'forgotSent' && t('authGate.subtitleForgotSent')}
        </p>

        {/* "Check your inbox" success screen */}
        {mode === 'forgotSent' && (
          <div className="text-center py-4" data-testid="auth-gate-forgot-sent">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-sm text-gray-700 mb-1 font-medium">{email}</p>
            <p className="text-xs text-gray-500 mb-5 leading-relaxed">{t('authGate.forgotSentBody')}</p>
            <button
              type="button"
              onClick={goBackToSignIn}
              data-testid="auth-gate-back-to-signin"
              className="text-sm text-blue-600 font-semibold hover:underline inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('authGate.backToSignIn')}
            </button>
          </div>
        )}

        {/* Forgot password form (email only) */}
        {mode === 'forgot' && (
          <form onSubmit={handleForgotSubmit} className="space-y-3" data-testid="auth-gate-forgot-form">
            <div>
              <label htmlFor="auth-gate-forgot-email" className="block text-xs font-medium text-gray-600 mb-1">
                {t('authGate.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  id="auth-gate-forgot-email"
                  data-testid="auth-gate-forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('authGate.emailPlaceholder')}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>
            </div>
            {error && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="auth-gate-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="auth-gate-forgot-submit"
              className="w-full px-4 py-2.5 min-h-[44px] bg-midnight-900 hover:bg-midnight-800 text-ivory-50 rounded-xl font-semibold shadow-md shadow-midnight-900/25 hover:shadow-lg hover:shadow-midnight-900/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('authGate.sendResetLink')}
            </button>
            <button
              type="button"
              onClick={goBackToSignIn}
              data-testid="auth-gate-cancel-forgot"
              className="w-full text-center text-xs text-gray-500 hover:text-gray-700 inline-flex items-center justify-center gap-1 pt-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t('authGate.backToSignIn')}
            </button>
          </form>
        )}

        {/* OAuth + email signup/signin (only on signup/signin modes) */}
        {(mode === 'signup' || mode === 'signin') && (<>

        {/* Google — primary path. Lead with the button, back it with clear,
            demystifying microcopy so users don't fear a surprise redirect. */}
        <button
          type="button"
          onClick={handleGoogle}
          data-testid="auth-gate-google-btn"
          className="w-full px-4 py-3 min-h-[52px] bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center gap-3 font-medium text-[15px] text-gray-800 hover:border-midnight-900 hover:shadow-md hover:shadow-midnight-900/10 active:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-midnight-900 focus-visible:ring-offset-2 transition-all shadow-sm"
        >
          <GoogleIcon />
          <span>{t('authGate.continueWithGoogle')}</span>
        </button>

        {/* Reassurance line — sits directly under the button so users read it
            before deciding to tap. Emphasises: password never touches us. */}
        <p
          className="mt-2.5 text-[11.5px] leading-relaxed text-center text-gray-600 px-2"
          data-testid="auth-gate-google-reassurance"
        >
          <ShieldCheck className="inline w-3.5 h-3.5 text-emerald-600 -mt-0.5 mr-1" />
          {t('authGate.googleReassurance')}
        </p>

        {/* Three literal, verifiable trust bullets — kills the "what will
            Google share?" anxiety in one glance. */}
        <ul
          className="mt-3 mb-1 space-y-1.5 rounded-xl bg-ivory-50/60 border border-gold-200/40 px-4 py-3"
          data-testid="auth-gate-google-benefits"
        >
          <li className="flex items-start gap-2 text-[12px] text-midnight-800/80 leading-snug">
            <KeyRound className="w-3.5 h-3.5 mt-0.5 text-gold-500 flex-shrink-0" />
            <span>{t('authGate.googleBenefit1')}</span>
          </li>
          <li className="flex items-start gap-2 text-[12px] text-midnight-800/80 leading-snug">
            <UserCircle2 className="w-3.5 h-3.5 mt-0.5 text-gold-500 flex-shrink-0" />
            <span>{t('authGate.googleBenefit2')}</span>
          </li>
          <li className="flex items-start gap-2 text-[12px] text-midnight-800/80 leading-snug">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-gold-500 flex-shrink-0" />
            <span>{t('authGate.googleBenefit3')}</span>
          </li>
        </ul>

        {/* Standards chip — subtle authority marker */}
        <p
          className="mt-2 mb-4 text-[10px] uppercase tracking-widest font-semibold text-center text-gray-400"
          data-testid="auth-gate-oauth-badge"
        >
          {t('authGate.oauthBadge')}
        </p>

        {/* P0 trust row — verifiable capabilities only. No card is ever collected here.
            All bookings redirect to IATA-accredited partners for payment. */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mb-4 text-[10px] uppercase tracking-widest font-semibold text-gray-500"
          data-testid="auth-gate-trust-row"
        >
          <span className="inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-emerald-600" />
            {t('authGate.trust.ssl')}
          </span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <BadgeCheck className="w-3 h-3 text-emerald-600" />
            {t('authGate.trust.gdpr')}
          </span>
          <span aria-hidden>·</span>
          <span className="inline-flex items-center gap-1">
            <Lock className="w-3 h-3 text-emerald-600" />
            {t('authGate.trust.noCard')}
          </span>
        </div>

        {/* Divider — de-emphasises email path so Google feels like the default */}
        <div className="flex items-center gap-3 my-4" aria-hidden="true">
          <span className="flex-1 h-px bg-gray-200" />
          <span className="text-[11px] tracking-wider text-gray-400 italic font-serif">{t('authGate.or')}</span>
          <span className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Email + password form */}
        <form onSubmit={handleEmailSubmit} className="space-y-3" data-testid="auth-gate-email-form">
          {mode === 'signup' && (
            <div>
              <label htmlFor="auth-gate-name" className="block text-xs font-medium text-gray-600 mb-1">
                {t('authGate.name')}
              </label>
              <input
                id="auth-gate-name"
                data-testid="auth-gate-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('authGate.namePlaceholder')}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="name"
              />
            </div>
          )}

          <div>
            <label htmlFor="auth-gate-email" className="block text-xs font-medium text-gray-600 mb-1">
              {t('authGate.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="auth-gate-email"
                data-testid="auth-gate-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="auth-gate-password" className="block text-xs font-medium text-gray-600 mb-1">
              {t('authGate.password')}
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                id="auth-gate-password"
                data-testid="auth-gate-password-input"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                minLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? t('common.close') : t('common.search')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-700"
              >
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {/* Password-strength meter — signup only. Hidden until the user types. */}
            {mode === 'signup' && password.length > 0 && (
              <div className="mt-2" data-testid="auth-gate-pwd-strength">
                <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${pwdMeter.bar}`} />
                </div>
                <p className={`mt-1 text-[11px] font-medium ${pwdMeter.text}`}>{pwdMeter.label}</p>
              </div>
            )}
          </div>

          {/* Remember me — the checkbox itself always renders in signup/signin
              so the two modes look consistent. On signup we hide the label
              hint but keep the state on by default (30-day session). */}
          {(mode === 'signin' || mode === 'signup') && (
            <label
              className="flex items-start gap-2.5 cursor-pointer select-none pt-1"
              data-testid="auth-gate-remember-me-label"
            >
              <input
                type="checkbox"
                data-testid="auth-gate-remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-midnight-900 rounded border-gray-300 focus:ring-2 focus:ring-gold-400 focus:ring-offset-1 cursor-pointer"
              />
              <span className="flex-1">
                <span className="block text-[13px] text-midnight-900 font-medium leading-tight">
                  {t('authGate.rememberMe')}
                </span>
                <span className="block text-[11px] text-gray-500 leading-tight mt-0.5">
                  {t('authGate.rememberMeHint')}
                </span>
              </span>
            </label>
          )}

          {error && (
            <p data-testid="auth-gate-error" className="text-xs text-red-600 text-center bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-gate-submit-btn"
            className="w-full px-4 py-2.5 min-h-[44px] bg-midnight-900 hover:bg-midnight-800 text-ivory-50 rounded-xl font-semibold shadow-md shadow-midnight-900/25 hover:shadow-lg hover:shadow-midnight-900/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'signup' ? t('authGate.createAccount') : t('authGate.signIn')}
          </button>

          {/* Forgot-password link — visible only in signin mode */}
          {mode === 'signin' && (
            <p className="text-center pt-1">
              <button
                type="button"
                onClick={goToForgot}
                data-testid="auth-gate-forgot-link"
                className="text-xs text-midnight-700 hover:text-midnight-900 hover:underline font-medium"
              >
                {t('authGate.forgotPassword')}
              </button>
            </p>
          )}
        </form>

        <p className="text-center text-sm text-gray-500 mt-4">
          {mode === 'signup' ? t('authGate.haveAccount') : t('authGate.noAccount')}{' '}
          <button
            type="button"
            onClick={switchMode}
            data-testid="auth-gate-toggle-mode"
            className="text-midnight-900 font-semibold hover:underline"
          >
            {mode === 'signup' ? t('authGate.signIn') : t('authGate.createAccount')}
          </button>
        </p>
        </>)}

        <p className="text-[11px] text-center text-gray-400 mt-3 leading-relaxed">
          {t('authGate.terms')}
        </p>
      </div>
    </div>,
    document.body
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.6 16.1 18.9 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.3 0 10.2-2 13.8-5.4l-6.4-5.3c-2 1.3-4.5 2.1-7.4 2.1-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.3 39.5 16.1 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.4 5.3c-.4.4 6.5-4.7 6.5-15 0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
